import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { StarMark } from "@/components/Logo";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS = "w-full rounded border border-ek-ink/15 bg-white px-3 py-2.5 text-sm text-ek-ink focus:border-ek-purple focus:outline-none focus:ring-1 focus:ring-ek-purple/30";

const ROLE_OPTIONS = [
  { v: "CRO", l: "CRO / Head of Risk" },
  { v: "CISO", l: "CISO / Head of Security" },
  { v: "RM", l: "Risk Manager" },
  { v: "Board", l: "Board / NED" },
  { v: "Other", l: "Other" },
];

/**
 * Public access-request form (iter110).
 *
 * Anonymous endpoint — captures interest from the marketing landing.
 * On submit, lands in `access_requests` collection in `pending` state
 * for the Owner / org_admin to action via `/admin/access-requests`.
 */
export default function RequestAccess() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [roleInterest, setRoleInterest] = useState("CRO");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.includes("@")) {
      toast.error("Please provide your name and a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/access-requests`, {
        name,
        email: email.trim().toLowerCase(),
        company,
        role_interest: roleInterest,
        message,
      });
      setSubmitted(true);
      toast.success(
        data?.deduplicated
          ? "You've already requested access — we'll be in touch shortly."
          : "Request received — we'll be in touch within 1 working day.",
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Request access" description="Request access to the EKSELENS · ERIO Risk Decision-Intelligence Layer." path="/request-access" />

      <header className="sticky top-0 z-40 border-b border-white/10 bg-black">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="request-access-home-link">
            <StarMark size={26} className="text-ek-lilac" />
            <span className="font-display text-lg font-bold text-white">EKSELENS <span className="text-ek-lilac">· ERIO</span></span>
          </Link>
          <Link to="/login" data-testid="request-access-signin-link" className="rounded-sm border border-white/15 px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:border-ek-lilac hover:text-ek-lilac">
            Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-ek-purple">Get in touch</p>
        <h1 className="mt-2 font-display text-4xl font-bold text-ek-ink sm:text-5xl">Request access</h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-ek-ink/75">
          We are running a limited pilot programme. Tell us a little about you and your organisation and a member of the Ekselens team will be in touch within one working day to arrange a 30-minute walkthrough.
        </p>

        {submitted ? (
          <div className="mt-10 rounded-md border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="request-access-success">
            <CheckCircle2 size={48} className="mx-auto mb-3 text-emerald-600" />
            <h2 className="font-display text-2xl font-bold text-ek-ink">Thank you, {name.split(" ")[0]}.</h2>
            <p className="mt-3 text-ek-ink/70">
              Your request has been logged. We will reach out shortly using <b>{email}</b>.
            </p>
            <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-ek-purple hover:underline">
              <ArrowRight size={14} /> Back to the home page
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="request-access-form">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Your name" required>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="request-access-name" required placeholder="Jane Smith" className={INPUT_CLS} />
              </Field>
              <Field label="Work email" required>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="request-access-email" required placeholder="jane.smith@yourcompany.com" className={INPUT_CLS} />
              </Field>
            </div>
            <Field label="Organisation">
              <input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="request-access-company" placeholder="Your company name" className={INPUT_CLS} />
            </Field>
            <Field label="Your role">
              <select value={roleInterest} onChange={(e) => setRoleInterest(e.target.value)} data-testid="request-access-role" className={INPUT_CLS}>
                {ROLE_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>{o.l}</option>
                ))}
              </select>
            </Field>
            <Field label="What would you like to discuss? (optional)">
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="request-access-message" placeholder="e.g. We are looking to consolidate three quarterly board papers into one risk decision-intelligence layer…" className={INPUT_CLS} maxLength={1000} />
            </Field>
            <button type="submit" disabled={submitting} data-testid="request-access-submit" className="ek-btn flex items-center gap-2 disabled:opacity-50 min-w-[180px] justify-center">
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}
              {submitting ? "Sending…" : "Send request"}
            </button>
            <p className="text-xs text-ek-ink/55">
              We only use the details above to respond to your enquiry. See our <Link to="/privacy" className="underline">Privacy Notice</Link>.
            </p>
          </form>
        )}
      </main>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wider text-ek-ink/65">
        {label} {required && <span className="text-ek-purple">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
